import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import { custom } from '../custom';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { getCurrentLocation } from '../utils/locationUtils';
import { compressImage } from '../utils/imageUtils';
import {
  MdLocationOn, MdAddPhotoAlternate, MdDelete,
  MdCheckCircle, MdError, MdWarning, MdBusiness,
  MdGroups, MdCalendarMonth,
} from 'react-icons/md';

const LocationIcon  = MdLocationOn as any;
const AddPhotoIcon  = MdAddPhotoAlternate as any;
const DeleteIcon    = MdDelete as any;
const CheckIcon     = MdCheckCircle as any;
const ErrorIcon     = MdError as any;
const WarningIcon   = MdWarning as any;
const BusinessIcon  = MdBusiness as any;
const GroupsIcon    = MdGroups as any;
const CalendarIcon  = MdCalendarMonth as any;

export const CreateSurveyActivity: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t } = useLanguage();
  const ts = (t as any).surveyActivity || {};

  const getDefaultDate = () => new Date().toISOString().slice(0, 10);

  const [surveyDate, setSurveyDate]           = useState(getDefaultDate());
  const [locationName, setLocationName]       = useState('');
  const [address, setAddress]                 = useState('');
  const [location, setLocation]               = useState('');
  const [locationError, setLocationError]     = useState('');
  const [isFetchingLocation, setIsFetchingLocation] = useState(true);
  const [respondentCount, setRespondentCount] = useState('');
  const [findings, setFindings]               = useState('');
  const [notes, setNotes]                     = useState('');
  const [imageFiles, setImageFiles]           = useState<File[]>([]);
  const [loading, setLoading]                 = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandPreview, setExpandPreview]     = useState<string | null>(null);

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean; title: string; message: string;
    type: 'success' | 'error' | 'warning'; onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  useEffect(() => {
    setTimeout(() => { window.scroll({ top: -1, left: 0, behavior: 'smooth' }); }, 10);
  }, [pathname]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const loc = await getCurrentLocation({ addressFormat: 'full' });
        setLocation(`${loc.lat},${loc.lng}`);
        setAddress(loc.address || 'Address not found');
      } catch (err: any) {
        setLocationError(err.message || 'Failed to get location. Please enable GPS.');
      } finally {
        setIsFetchingLocation(false);
      }
    };
    fetchLocation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({ isOpen: true, title, message, type, onClose });
  };
  const hideAlert = () => {
    const cb = alertConfig.onClose;
    setAlertConfig(prev => ({ ...prev, isOpen: false }));
    if (cb) cb();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const newFiles = await Promise.all(Array.from(e.target.files).map(async (file) => {
      if (file.type.startsWith('image/')) {
        try { return await compressImage(file); } catch { return file; }
      }
      return file;
    }));
    setImageFiles(prev => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    if (expandPreview && imageFiles[index] && URL.createObjectURL(imageFiles[index]) === expandPreview) {
      setExpandPreview(null);
    }
  };

  const handleSubmit = async () => {
    if (!surveyDate) {
      showAlert(t.common?.warning || 'Warning', ts.validationDate || 'Survey date is required.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('survey_date', surveyDate);
      if (locationName) formData.append('location_name', locationName);
      if (address) formData.append('address', address);
      if (location) formData.append('location', location);
      if (respondentCount) formData.append('respondent_count', respondentCount);
      if (findings) formData.append('findings', findings);
      if (notes) formData.append('notes', notes);
      imageFiles.forEach(file => formData.append('images', file));

      await axiosInstance.post('/survey-activities', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      showAlert(
        t.common?.success || 'Success',
        ts.createSuccess || 'Survey activity recorded successfully!',
        'success',
        () => navigate(-1)
      );
    } catch (error) {
      console.error('Failed to create survey activity:', error);
      showAlert(t.common?.error || 'Error', ts.createError || 'Failed to record survey. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #E5E7EB',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    backgroundColor: '#fff',
    fontFamily: 'inherit',
    resize: 'none',
  };

  return (
    <div id="screen">
      <components.Header title={ts.createTitle || 'Record Survey'} goBack={true} />

      <div style={{ padding: 0 }}>
      <main style={{ marginTop: 62, paddingBottom: 100, backgroundColor: '#F9FAFB' }} className="container">

        {/* Survey Date */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>
            {ts.surveyDate || 'Survey Date'} *
          </text.H5>
          <custom.InputField
            type={"date" as any}
            value={surveyDate}
            onChange={(e: any) => setSurveyDate(e.target.value)}
            leftIcon={<CalendarIcon />}
            containerStyle={{ marginBottom: 0 }}
          />
        </div>

        {/* Location Name */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>
            {ts.locationName || 'Survey Location / Company'}
          </text.H5>
          <custom.InputField
            placeholder={ts.locationNamePlaceholder || 'e.g. PT ABC, Pasar Tradisional X'}
            value={locationName}
            onChange={(e: any) => setLocationName(e.target.value)}
            leftIcon={<BusinessIcon />}
            containerStyle={{ marginBottom: 0 }}
          />
        </div>

        {/* GPS Location */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>
            {t.common?.location || 'GPS Location'}
          </text.H5>
          <div style={{
            padding: 14, backgroundColor: '#fff', borderRadius: 10,
            border: '1px solid #E5E7EB', display: 'flex', alignItems: 'flex-start', gap: 10,
            minHeight: 80,
          }}>
            <LocationIcon size={20} color={theme.colors.mainColor} style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {isFetchingLocation ? (
                <text.T14 style={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                  {t.taskSubmission?.fetchingLocation || 'Getting location...'}
                </text.T14>
              ) : locationError ? (
                <text.T14 style={{ color: '#EF4444' }}>{locationError}</text.T14>
              ) : (
                <>
                  <text.T14 style={{ color: theme.colors.mainDark, fontWeight: '500' }}>{address}</text.T14>
                  <text.T14 style={{ color: '#9CA3AF', fontSize: 11, marginTop: 2 }}>{location}</text.T14>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Respondent Count */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>
            {ts.respondentCount || 'Number of Respondents'}
          </text.H5>
          <custom.InputField
            type={"number" as any}
            placeholder={ts.respondentCountPlaceholder || 'e.g. 10'}
            value={respondentCount}
            onChange={(e: any) => setRespondentCount(e.target.value)}
            leftIcon={<GroupsIcon />}
            containerStyle={{ marginBottom: 0 }}
          />
        </div>

        {/* Findings */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>
            {ts.findings || 'Survey Findings'}
          </text.H5>
          <div style={{ backgroundColor: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', padding: '4px 16px' }}>
            <textarea
              placeholder={ts.findingsPlaceholder || 'Describe the key findings from this survey...'}
              value={findings}
              onChange={e => setFindings(e.target.value)}
              rows={4}
              style={{ ...inputStyle, border: 'none', padding: '10px 0' }}
            />
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>
            {ts.notes || 'Additional Notes'}
          </text.H5>
          <div style={{ backgroundColor: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', padding: '4px 16px' }}>
            <textarea
              placeholder={ts.notesPlaceholder || 'Any additional notes or observations...'}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              style={{ ...inputStyle, border: 'none', padding: '10px 0' }}
            />
          </div>
        </div>

        {/* Photo Upload */}
        <div style={{ marginBottom: 24 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>
            {ts.photos || 'Survey Photos'}
          </text.H5>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
            {imageFiles.map((file, idx) => {
              const url = URL.createObjectURL(file);
              return (
                <div key={idx} style={{ position: 'relative', width: 100, height: 100, flexShrink: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                  <img
                    src={url}
                    alt=""
                    onClick={() => setExpandPreview(url)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                  />
                  <div
                    onClick={() => removeImage(idx)}
                    style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                  >
                    <DeleteIcon size={14} color="#fff" />
                  </div>
                </div>
              );
            })}
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: 100, height: 100, flexShrink: 0,
                border: '2px dashed #D1D5DB', borderRadius: 10,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#F9FAFB', cursor: 'pointer', gap: 4,
              }}
            >
              <AddPhotoIcon size={28} color="#9CA3AF" />
              <text.T14 style={{ fontSize: 11, color: '#9CA3AF' }}>{ts.addPhoto || 'Add Photo'}</text.T14>
            </div>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept="image/*"
            multiple
          />
        </div>
      </main>
      </div>

      {/* Submit button */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        backgroundColor: 'white', padding: '16px 24px 32px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.05)', zIndex: 10,
      }}>
        <div className="container" style={{ padding: 0, maxWidth: 650, margin: '0 auto' }}>
          <components.Button
            title={loading ? (t.common?.saving || 'Saving...') : (ts.submit || 'Save Survey')}
            onClick={handleSubmit}
          />
        </div>
      </div>

      {/* Image preview overlay */}
      {expandPreview && (
        <div
          onClick={() => setExpandPreview(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <img src={expandPreview} alt="" style={{ maxWidth: '95vw', maxHeight: '90vh', borderRadius: 8, objectFit: 'contain' }} />
        </div>
      )}

      {/* Alert */}
      {alertConfig.isOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 320, textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ marginBottom: 16 }}>
              {alertConfig.type === 'success' && <CheckIcon size={60} color="#10B981" />}
              {alertConfig.type === 'error'   && <ErrorIcon size={60} color="#EF4444" />}
              {alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}
            </div>
            <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
            <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>{alertConfig.message}</text.T16>
            <components.Button title={t.common?.ok || 'OK'} onClick={hideAlert} containerStyle={{ width: '100%' }} />
          </div>
        </div>
      )}
    </div>
  );
};
